// ─── Guided Setup: shared pieces ──────────────────────────────────────────────
//
// Lifted out of GuidedSetupPage so the industrial module panels (attendance,
// guard patrol, access correlation) are built from the same parts as the rest of
// the wizard. They answer the same kind of question and should not look like a
// different product halfway through setup.
//
// Styling is the gs-* classes in css/guided-setup.css, which the wizard page
// already imports — these render inside it.

import { useState } from 'react';
import PatrolZoneEditor from '../PatrolZoneEditor';

export function Field({ label, help, children, helpAfter }) {
  return (
    <div className="gs-field">
      {label && <label className="gs-label">{label}</label>}
      {help && <p className="gs-help">{help}</p>}
      {children}
      {helpAfter && <p className="gs-help gs-help-after">{helpAfter}</p>}
    </div>
  );
}

export function Choice({ icon, title, sub, selected, onClick }) {
  return (
    <button type="button" className={`gs-choice${selected ? ' selected' : ''}`} onClick={onClick}>
      {icon && <span className="gs-choice-icon">{icon}</span>}
      <span>{title}</span>
      {sub && <span className="gs-choice-sub">{sub}</span>}
    </button>
  );
}

// A two-or-three option answer, shown as a segmented pair rather than a dropdown.
//
// A <select> with two options is the wrong control: the answer is hidden until
// you open it, it takes two taps to change, and on a phone it opens a native
// picker over the whole screen for a yes/no. Both answers are visible here, and
// one tap sets them.
//
// `value === undefined` renders neither as selected, which is how "not said yet"
// stays visibly different from an answer.
export function Pick({ value, options, onChange, disabled }) {
  return (
    <span className="gs-pick" role="group">
      {options.map(o => (
        <button key={String(o.value)} type="button" disabled={disabled}
                aria-pressed={value === o.value}
                className={`gs-pick-btn${value === o.value ? ' sel' : ''}`}
                onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </span>
  );
}

export function Toggle({ on, onChange, title, sub }) {
  return (
    <label className={`gs-toggle${on ? ' on' : ''}`}>
      <input type="checkbox" checked={on} onChange={e => onChange(e.target.checked)} />
      <span className="gs-toggle-text">
        <span className="gs-toggle-title">{title}</span>
        {sub && <span className="gs-toggle-sub">{sub}</span>}
      </span>
    </label>
  );
}

export function Panel({ tone, title, children }) {
  return (
    <div className={`gs-panel${tone ? ` ${tone}` : ''}`}>
      {title && <div className="gs-panel-title">{title}</div>}
      {children}
    </div>
  );
}

// One tunable, driven by a spec from utils/aiModels.js (or the same shape built
// by hand for a module setting).
export function SettingRow({ spec, value, onChange, overridden }) {
  const val = value != null ? value : spec.def;
  return (
    <div className="gs-set-row">
      <label>{spec.label}{overridden && <span className="gs-set-flag"> · changed</span>}</label>
      {spec.type === 'bool' ? (
        <>
          <input type="checkbox" checked={!!val} onChange={e => onChange(e.target.checked)}
                 style={{ width: 18, height: 18 }} />
          <span className="gs-set-val">{val ? 'On' : 'Off'}</span>
        </>
      ) : spec.type === 'range' ? (
        <>
          <input type="range" min={spec.min} max={spec.max} step={spec.step} value={val}
                 onChange={e => onChange(parseFloat(e.target.value))} />
          <span className="gs-set-val">{val}</span>
        </>
      ) : spec.type === 'select' ? (
        <>
          <select className="gs-input" value={val} onChange={e => onChange(e.target.value)}
                  style={{ maxWidth: 220 }}>
            {(spec.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <span className="gs-set-val" />
        </>
      ) : spec.type === 'time' ? (
        <>
          <input className="gs-input" type="time" value={val || ''}
                 onChange={e => onChange(e.target.value)} style={{ maxWidth: 150 }} />
          <span className="gs-set-val" />
        </>
      ) : (
        <>
          <input className="gs-input" type="number" min={spec.min} max={spec.max} step={spec.step}
                 value={val}
                 onChange={e => onChange(parseInt(e.target.value, 10) || spec.min)} />
          <span className="gs-set-val" />
        </>
      )}
      {spec.hint && <span className="gs-set-hint">{spec.hint}</span>}
    </div>
  );
}

// Which days a schedule runs. Monday-first, because the box tests
// datetime.weekday() — Monday is 0 — and a picker that put Sunday first would
// write Saturday's number for the day the customer tapped.
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function DayPicker({ value, onChange }) {
  const days = Array.isArray(value) && value.length ? value : [0, 1, 2, 3, 4, 5, 6];
  const flip = d => onChange(days.includes(d) ? days.filter(x => x !== d) : [...days, d].sort());
  return (
    <div className="gs-chips">
      {DAY_LABELS.map((label, d) => (
        <button key={d} type="button" className={`gs-chip${days.includes(d) ? ' on' : ''}`}
                onClick={() => flip(d)}>{label}</button>
      ))}
      <button type="button" className="gs-btn small ghost"
              onClick={() => onChange(days.length === 7 ? [0, 1, 2, 3, 4] : [0, 1, 2, 3, 4, 5, 6])}>
        {days.length === 7 ? 'Weekdays only' : 'Every day'}
      </button>
    </div>
  );
}

// Pick cameras for something. A chip per camera rather than a row per camera:
// the answer is usually two or three out of a plant's twenty.
export function CameraChips({ cameras, value, onChange, single }) {
  const sel = Array.isArray(value) ? value : (value == null ? [] : [value]);
  const flip = idx => {
    if (single) { onChange(idx); return; }
    onChange(sel.includes(idx) ? sel.filter(i => i !== idx) : [...sel, idx]);
  };
  return (
    <div className="gs-chips">
      {cameras.map(c => (
        <button key={c.idx} type="button"
                className={`gs-chip${sel.includes(c.idx) ? ' on' : ''}`}
                onClick={() => flip(c.idx)}>
          {c.name}
          {c.enabled === false && <span className="gs-chip-off">off</span>}
        </button>
      ))}
    </div>
  );
}

// Settings that already have a good answer, kept out of the way.
//
// The wizard's first rule is to ask only what is required. A shift that starts at
// 08:30, a 90-day retention, a 120-second dedup window — these are right almost
// everywhere, and putting them to a customer who has no basis to answer them
// makes a setup screen longer AND worse. So they are collapsed, with the count of
// anything already changed on the summary line, because a hidden setting that
// has been altered must never be invisible.
export function Advanced({ title = 'Advanced settings', changed = 0, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="gs-adv">
      <button type="button" className="gs-adv-head" onClick={() => setOpen(o => !o)}
              aria-expanded={open}>
        <span>{open ? '⌃' : '⌄'} {title}</span>
        <span className="gs-adv-note">
          {changed ? `${changed} changed` : 'all at defaults'}
        </span>
      </button>
      {open && <div className="gs-adv-body">{children}</div>}
    </div>
  );
}

// How many of these settings differ from the value the code ships with.
export function countChanged(specs, values = {}) {
  return specs.filter(s => {
    const v = values[s.key];
    return v !== undefined && v !== null && v !== s.def;
  }).length;
}

// A list of polygons on one camera — seat zones, or the staff areas behind a
// footfall counting line. Draw one, add it, draw the next. Shared because both
// are the same job and a second implementation would drift from the first.
export function PolygonList({ cameraIdx, value = [], onChange, noun = 'area', named }) {
  const [draft, setDraft] = useState([]);
  const [name,  setName]  = useState('');
  const list = Array.isArray(value) ? value : [];

  const add = () => {
    if (draft.length < 3) return;
    onChange([...list, named ? { name: name.trim() || `${noun} ${list.length + 1}`, polygon: draft }
                             : draft]);
    setDraft([]); setName('');
  };

  return (
    <>
      {list.map((z, i) => (
        <div className="gs-row" key={i}>
          <span className="gs-row-icon">📐</span>
          <span className="gs-row-text">
            <span className="gs-row-name">{named ? (z.name || `${noun} ${i + 1}`) : `${noun} ${i + 1}`}</span>
            <span className="gs-row-sub">
              {((named ? z.polygon : z) || []).length} corners
            </span>
          </span>
          <button className="gs-btn small ghost"
                  onClick={() => onChange(list.filter((_, n) => n !== i))}>Remove</button>
        </div>
      ))}

      {named && (
        <Field label={`Name this ${noun}`}>
          <input className="gs-input" value={name} maxLength={40}
                 placeholder={`${noun} ${list.length + 1}`}
                 onChange={e => setName(e.target.value)} />
        </Field>
      )}
      <PatrolZoneEditor cameraIdx={cameraIdx} value={draft} onChange={setDraft} />
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
        <button className="gs-btn" onClick={add} disabled={draft.length < 3}>
          Add this {noun}
        </button>
        {draft.length > 0 && draft.length < 3 &&
          <span className="gs-foot-note">Three corners is enough</span>}
      </div>
    </>
  );
}

// A module panel's save/err plumbing, so the three panels report a refusal,
// a network failure and a 403 the same way. A 403 is called out separately: it
// is not something the customer can fix by pressing the button again.
export function useModuleSaver(apiFetch, onChanged) {
  const [busy, setBusy] = useState(false);
  const [err,  setErr]  = useState(null);
  const [note, setNote] = useState(null);

  async function run(method, url, json, what) {
    setBusy(true); setErr(null); setNote(null);
    try {
      const r = await apiFetch(url, { method, ...(json !== undefined ? { json } : {}) });
      if (r.status === 403) {
        setErr('Your login is not allowed to change this. Ask an administrator to '
             + 'grant it, or to finish this part for you.');
        return null;
      }
      if (!r.ok) {
        const d = await r.json().catch(() => null);
        setErr(typeof d?.detail === 'string' ? d.detail : `Could not save ${what || 'that'}.`);
        return null;
      }
      const out = await r.json().catch(() => ({}));
      if (what) setNote(`Saved ${what}.`);
      if (onChanged) await onChanged();
      return out || {};
    } catch {
      setErr('Could not reach the box. Check the connection and try again.');
      return null;
    } finally {
      setBusy(false);
    }
  }

  return { busy, err, note, setErr, setNote, run };
}
